"use client"

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Grid } from "react-loader-spinner"

import { useAuth } from "@/components/auth/auth-provider"
import { SetupLayout } from "@/components/identity/setup-layout"
import { requestIdentitySession } from "@/lib/identity-session"
import {
  subscribeIdentitySetupChanged,
  type IdentitySetup,
  type SessionClientBootstrap,
} from "@/lib/identity-setup"
import { WebsocketProvider } from "@/components/websocket/websocket-provider"
import { NowCallingOverlay } from "@/components/now-calling-overlay"
import { ActiveCallProvider } from "@/lib/active-call"
import { sessionBootstrapReceived, useAppDispatch } from "@/lib/store"

const defaultSetup: IdentitySetup = {
  required: false,
  workspace: false,
  user: false,
}

export function IdentitySetupGate({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch()
  const { logout } = useAuth()
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading")
  const [setup, setSetup] = useState<IdentitySetup>(defaultSetup)
  const [bootstrap, setBootstrap] = useState<SessionClientBootstrap | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setPhase("loading")
    setErrorMessage(null)
    try {
      const { setup: next, bootstrap: nextBootstrap } = await requestIdentitySession(false)
      dispatch(sessionBootstrapReceived(nextBootstrap))
      setBootstrap(nextBootstrap)
      setSetup(next)
      setPhase("ready")
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e))
      setPhase("error")
    }
  }, [dispatch])

  useEffect(() => {
    const id = window.setTimeout(() => {
      void refresh()
    }, 0)
    return () => window.clearTimeout(id)
  }, [refresh])

  const completeSetup = useCallback(async () => {
    const { setup: parsed, bootstrap: nextBootstrap } = await requestIdentitySession(true)
    dispatch(sessionBootstrapReceived(nextBootstrap))
    setBootstrap(nextBootstrap)
    setSetup(parsed)
    // When setup finishes, the BFF returns 303 + FRONTEND_POST_SETUP_URL; the command client assigns `location`.
  }, [dispatch])

  useEffect(() => {
    return subscribeIdentitySetupChanged(setSetup)
  }, [])

  if (phase === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-8">
        <Grid
          visible={true}
          height="40"
          width="40"
          color="#7dd3fc"
          ariaLabel="grid-loading"
          radius="12.5"
          wrapperStyle={{}}
          wrapperClass="grid-wrapper"
        />
      </main>
    )
  }

  if (phase === "error") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-100 p-8">
        <p className="max-w-md text-center text-sm text-red-800">{errorMessage ?? "Unknown error"}</p>
        <button
          type="button"
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700"
          onClick={() => void refresh()}
        >
          Retry
        </button>
        <button
          type="button"
          className="text-sm text-slate-600 underline"
          onClick={() => void logout()}
        >
          Log out
        </button>
      </main>
    )
  }

  const content = setup.required ? (
    <SetupLayout setup={setup} onRetry={refresh} onLogout={logout} onCompleteSetup={completeSetup} />
  ) : (
    children
  )

  return (
    <WebsocketProvider bootstrap={bootstrap}>
      <ActiveCallProvider>
        {content}
        <NowCallingOverlay />
      </ActiveCallProvider>
    </WebsocketProvider>
  )
}
